import { inject, Injector, Service } from '@angular/core';
import type { OnyxPaginated, OnyxPagination } from '@onyx/angular';
import { ONYX_TOASTS_LIMIT, OnyxToastService } from '@onyx/angular';
import type { Observable, OperatorFunction } from 'rxjs';
import { catchError, EMPTY, Subject, tap, throwError } from 'rxjs';
import type { Activity } from '../../../../common/interfaces/common/activity';
import { ApiService } from '../../../../common/services/api.service';
import { TelematicsDataService } from '../../../../common/services/telematics-data.service';
import { DriversService } from '../../../drivers/common/services/drivers.service';
import { FleetService } from '../../../fleet/common/services/fleet.service';
import type { DelegationActionModalFormDto } from '../../delegation-action-modal/delegation-action-modal.component';
import type { DelegationCategory } from '../enums/delegation-category';
import type { DelegationStatus } from '../enums/delegation-status';
import type { DelegationDto } from '../helpers/delegation-form.helper';
import { DelegationFormHelper } from '../helpers/delegation-form.helper';
import type { Delegation } from '../interfaces/delegation';
import { JoinPipe } from './../../../../common/pipes/join.pipe';

export interface DelegationsFilters {
  query?: string;
  category?: DelegationCategory | 'all';
  statuses?: DelegationStatus[];
  hasDoubleCrew?: boolean;
  showAssignedOnly?: boolean;
}

@Service()
export class DelegationsService extends ApiService {
  private readonly toastService = inject(OnyxToastService);
  private readonly fleetService = inject(FleetService);
  private readonly joinPipe = inject(JoinPipe);
  private readonly injector = inject(Injector);
  private readonly driversService = inject(DriversService);

  private _reload$ = new Subject<void>();
  public get reload$() {
    return this._reload$.asObservable();
  }

  public listDelegations(
    params: OnyxPagination & DelegationsFilters,
  ): Observable<OnyxPaginated<Delegation>> {
    return this.get('/delegations', {
      params: {
        page: params.page,
        limit: params.limit,
        ...(params.category &&
          params.category !== 'all' && { category: params.category }),
        ...(params.statuses?.length && { 'status[]': params.statuses }),
        ...(params.hasDoubleCrew && { hasDoubleCrew: params.hasDoubleCrew }),
        ...(params.showAssignedOnly != null && {
          showAssignedOnly: params.showAssignedOnly,
        }),
        ...(params.query && { query: params.query }),
      },
    });
  }

  public getDelegation(uuid: string): Observable<Delegation> {
    return this.get(`/delegations/${uuid}`);
  }

  public addDelegation(delegation: DelegationDto): Observable<Delegation> {
    return this.post<Delegation>('/delegations', delegation).pipe(
      this.catchDelegationErrors(),
      tap(() => this.reloadRelated()),
    );
  }

  public editDelegation(
    uuid: string,
    delegation: DelegationDto,
  ): Observable<Delegation> {
    return this.put<Delegation>(`/delegations/${uuid}`, delegation).pipe(
      this.catchDelegationErrors(),
      tap(() => this.reloadRelated()),
    );
  }

  public archiveDelegation(uuid: string): Observable<void> {
    return this.post<void>(`/delegations/${uuid}/archive`, undefined).pipe(
      tap(() => this.reloadRelated()),
    );
  }

  public unarchiveDelegation(uuid: string): Observable<void> {
    return this.post<void>(`/delegations/${uuid}/unarchive`, undefined).pipe(
      tap(() => this.reloadRelated()),
    );
  }

  public activateDelegation(
    uuid: string,
    form: DelegationActionModalFormDto,
  ): Observable<Delegation> {
    return this.post<Delegation>(`/delegations/${uuid}/activate`, form).pipe(
      this.catchDelegationErrors(),
      tap((delegation) => this.updateTelematicsDelegation(delegation)),
      tap(() => this.reloadRelated()),
    );
  }

  public finishDelegation(
    uuid: string,
    form: DelegationActionModalFormDto,
  ): Observable<Delegation> {
    return this.post<Delegation>(`/delegations/${uuid}/finish`, form).pipe(
      this.catchDelegationErrors(),
      tap((delegation) => this.updateTelematicsDelegation(delegation)),
      tap(() => this.reloadRelated()),
    );
  }

  public settleDelegation(uuid: string) {
    return this.post<void>(`/delegations/${uuid}/settle`, undefined).pipe(
      this.catchDelegationErrors(),
      tap(() => this.reloadRelated()),
    );
  }

  public returnDelegation(
    uuid: string,
    form: DelegationActionModalFormDto,
  ): Observable<Delegation> {
    return this.post<Delegation>(`/delegations/${uuid}/return`, form).pipe(
      this.catchDelegationErrors(),
      tap((delegation) => this.updateTelematicsDelegation(delegation)),
      tap(() => this.reloadRelated()),
    );
  }

  public cancelReturnDelegation(uuid: string): Observable<Delegation> {
    return this.delete<Delegation>(`/delegations/${uuid}/return`).pipe(
      this.catchDelegationErrors(),
      tap((delegation) => this.updateTelematicsDelegation(delegation)),
      tap(() => this.reloadRelated()),
    );
  }

  public cancelDelegation(uuid: string) {
    return this.post<void>(`/delegations/${uuid}/cancel`, undefined).pipe(
      this.catchDelegationErrors(),
      tap(() => this.reloadRelated()),
    );
  }

  public getDelegationActivity(uuid: string): Observable<Activity> {
    return this.get(`/delegations/${uuid}/activity`);
  }

  private catchDelegationErrors<T>(): OperatorFunction<T, T> {
    return catchError<T, Observable<T>>((response) => {
      const toasts = DelegationFormHelper.getDelegationErrorToasts(
        response,
        this.joinPipe,
      );
      if (!toasts.length) return throwError(() => response);

      for (const toast of toasts.slice(0, ONYX_TOASTS_LIMIT)) {
        this.toastService.showError(toast);
      }
      return EMPTY;
    });
  }

  private updateTelematicsDelegation(delegation: Delegation): void {
    this.injector
      .get(TelematicsDataService)
      .updateDelegation(delegation.vehicle.uuid, delegation);
  }

  private reloadRelated(): void {
    this._reload$.next();
    this.fleetService['_reload$'].next();
    this.driversService['_reload$'].next();
  }
}
